import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';

const CustomMultiSelect = ({
  value = [],
  onChange,
  options = [],
  isError,
  label,
  disabled,
  rightLabel,
  rightOnCLick,
  placeholder = "ALL",
  defaultLabel,
  required,
  className,
}) => {
  const imageBaseUrl = useSelector(state => state.config.image_base_url);
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);
  
  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  
  useOutsideClick(selectRef, () => {
    if (open) setOpen(false);
  });

  const handleToggle = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  // This handles item selection without closing the dropdown
  const handleSelect = (option, event) => {
    event.stopPropagation();
    
    // Check if option is already selected
    const isSelected = selectedValues.some(item => {
      if (typeof item === 'object' && typeof option === 'object') {
        return item.currencyShortName === option.currencyShortName;
      }
      return item === option;
    });
    
    // If selected, remove it; otherwise, add it
    let newSelectedValues;
    if (isSelected) {
      newSelectedValues = selectedValues.filter(item => {
        if (typeof item === 'object' && typeof option === 'object') {
          return item.currencyShortName !== option.currencyShortName;
        }
        return item !== option;
      });
    } else {
      newSelectedValues = [...selectedValues, option];
    }
    
    onChange(newSelectedValues);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange([]);
  };

  return (
    <div className={`relative w-full ${className || ''}`} ref={selectRef}>
      <label className="block text-sm text-balanceCard-manage mb-2 uppercase tracking-wide w-full text-start">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {rightLabel ? (
          <span data-e2e={rightLabel} onClick={rightOnCLick} className="float-right cursor-pointer text-widget">
            {rightLabel}
          </span>
        ) : null}
      </label>
      
      <div 
        className={`${
          options?.length > 1 && !disabled ? 'cursor-pointer' : ''
        } relative bg-card-bg !text-text rounded-md py-4 pl-14 pr-20 text-sm font-medium border-2 
        ${isError ? 'border-red-400' : 'border-transparent'}`}
        onClick={handleToggle}
      >
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {selectedValues.length === 1 && selectedValues[0]?.currencyIcon && (
            <img
              src={`${imageBaseUrl}${selectedValues[0].currencyIcon}`}
              alt=""
              className="w-5 h-5 rounded-full"
            />
          )}
          <span className="text-table-col-title text-sm uppercase">
            {selectedValues.length === 0 
              ? placeholder 
              : selectedValues.length === 1 
                ? (selectedValues[0]?.currencyShortName || selectedValues[0]) 
                : `${selectedValues.length} selected`
            }
          </span>
          {selectedValues.length === 1 && (selectedValues[0]?.currencyFullName || selectedValues[0]?.blockchain) ? (
            <span>
              ({selectedValues[0]?.currencyFullName || selectedValues[0]?.blockchain})
            </span>
          ) : defaultLabel && selectedValues.length === 0 ? (
            <div className="text-table-col-title">{defaultLabel}</div>
          ) : null}
        </div>

        {options?.length > 1 ? (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-white">
            {selectedValues.length > 0 && (
              <ClearIcon 
                fontSize="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
                className="mr-2 cursor-pointer text-balanceCard-manage"
              />
            )}
              {selectedValues.length === 0 && (
              open ? (
                <ExpandLessIcon fontSize="small" className="invert dark:invert-0" />
              ) : (
                <ExpandMoreIcon fontSize="small" className="invert dark:invert-0" />
              )
            )}
          </div>
        ) : null}
      </div>

      {open && !disabled && (
        <ul className="absolute w-full max-h-[200px] z-10 mt-1 bg-white dark:!bg-[#26272B] rounded-md shadow-lg overflow-auto !text-text text-sm hide-scrollbar">
          {options.map(opt => {
            const optionValue = typeof opt === 'object' ? opt.currencyShortName : opt;
            const isSelected = selectedValues.some(item => {
              if (typeof item === 'object' && typeof opt === 'object') {
                return item.currencyShortName === opt.currencyShortName;
              }
              return item === opt;
            });
            
            return (
              <li
                key={optionValue || opt.blockchain || opt}
                onClick={(e) => handleSelect(opt, e)}
                className={`px-4 py-2 cursor-pointer ${isSelected ? 'bg-card-bg' : 'hover:bg-card-bg'} flex items-center justify-between`}
              >
                <div className="flex items-center gap-2">
                  {opt.currencyIcon && (
                    <img
                      src={`${imageBaseUrl}${opt.currencyIcon}`}
                      alt=""
                      className="w-5 h-5 rounded-full"
                    />
                  )}
                  <span>{opt.currencyShortName || opt}</span>
                  {opt.currencyFullName || opt.blockchain ? (
                    <span>({opt.currencyFullName || opt.blockchain})</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

CustomMultiSelect.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        currencyIcon: PropTypes.string,
        currencyShortName: PropTypes.string,
        currencySymbol: PropTypes.string,
        currencyFullName: PropTypes.string,
      }),
    ])
  ),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        currencyIcon: PropTypes.string,
        currencyShortName: PropTypes.string,
        currencySymbol: PropTypes.string,
        currencyFullName: PropTypes.string,
      }),
    ])
  ).isRequired,
  isError: PropTypes.bool,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  rightLabel: PropTypes.string,
  rightOnCLick: PropTypes.func,
  placeholder: PropTypes.string,
  defaultLabel: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
};

export default CustomMultiSelect;;

